import * as React from "react";
import { z } from "zod";
import { DynamicForm } from "../../util/DynamicForm";
import { useGeneratedForm } from "../../hooks/useGeneratedForm";
import { apiPost } from "../../util/axiosUtil";
import API_URL from "../../config/apiConfig";
import MutationHandler from "../../util/MutationHandler";

const CREATE_MANAGER_URL = `${API_URL}/api/managers`;

const formConfig = {
  name: {
    label: "Name",
    type: "text",
    placeholder: "Enter your name",
    defaultValue: "",
    required: true,
    validation: z.string().superRefine((value, ctx) => {
      if (!value.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Name is required",
        });
      } else if (value.length < 4) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Name should be more than 3 letters",
        });
      }
    }),
  },
  username: {
    label: "Username",
    type: "text",
    placeholder: "Enter username",
    defaultValue: "",
    required: true,
    normalize: (val) => val.toLowerCase().replace(/\s+/g, ""),
    validation: z
      .string()
      .transform((val) => val.toLowerCase().replace(/\s+/g, "")) // ✅ auto-make lowercase and remove spaces
      .superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Username is required",
          });
        } else if (value.length < 3) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Username should be more than 3 letters",
          });
        } else if (!/^[a-z0-9_]+$/.test(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Username can only contain lowercase letters, numbers, and underscores",
          });
        }
      }),
  },
  password: {
    label: "Password",
    type: "password",
    placeholder: "Enter password",
    defaultValue: "",
    required: true,
    normalize: (val) => val.replace(/\s+/g, ""),
    validation: z.string().min(4, "Password must be at least 4 characters"),
  },
};

export default function CreateManager({ onCancel, onSuccess }) {
  return (
    <MutationHandler
      mutationFn={(formData) => apiPost(CREATE_MANAGER_URL, formData)}
      onCancel={onCancel}
      onSuccess={onSuccess}
    >
      {(mutation) => (
        <ManagerForm
          formConfig={formConfig}
          onSubmit={(value) => mutation.mutate(value)}
          isLoading={mutation.isPending}
          onCancel={onCancel}
        />
      )}
    </MutationHandler>
  );
}

function ManagerForm({ formConfig, onSubmit, isLoading, onCancel }) {
  const form = useGeneratedForm(formConfig, async ({ value }) => {
    onSubmit(value);
  });

  return (
    <DynamicForm
      form={form}
      formConfig={formConfig}
      isLoading={isLoading}
      onCancel={onCancel}
      buttonText={"Create Manager"}
    />
  );
}
